import posthog from 'posthog-js';
import { TRACKING_CONFIG, getStepInfo } from '../config/trackingConfig.js';

class TrackingService {
  constructor() {
    this.posthog = null;
    this.trackedSteps = new Set();
    this.trackedCompletions = new Set();
    this.initializePostHog();
  }

  initializePostHog() {
    try {
      this.posthog = posthog.init(TRACKING_CONFIG.POSTHOG_CONFIG.apiKey, {
        api_host: TRACKING_CONFIG.POSTHOG_CONFIG.apiHost,
        defaults: TRACKING_CONFIG.POSTHOG_CONFIG.defaults,
        person_profiles: 'always',
        autocapture: {
          dom_event_allowlist: [], // DOM events from this list ['click', 'change', 'submit']
          url_allowlist: [
              /^https:\/\/(www\.)?memoorias\.com$|^http:\/\/localhost:3000$/
          ], // strings or RegExps
          // url_ignorelist can be used on its own, or combined with url_allowlist to further filter which URLs are captured
          url_ignorelist: [], // strings or RegExps
          element_allowlist: [], // DOM elements from this list ['a', 'button', 'form', 'input', 'select', 'textarea', 'label']
          css_selector_allowlist: [], // List of CSS selectors
          element_attribute_ignorelist:[], // List of element attributes to ignore
        },
      });
    } catch (error) {
      console.error('Error initializing PostHog:', error);
    }
  }

  trackPageViewed(additionalData = {}) {
    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    this.posthog.capture(TRACKING_CONFIG.EVENTS.PAGE_VIEWED, {
      ...additionalData
    });
  }

  /**
   * Track step view with consistent data structure
   * @param {string} stepName - Name of the step being viewed
   * @param {Object} quotationData - Current quotation data
   * @param {Object} additionalData - Additional data to include in tracking
   */
  trackStepView(stepName, quotationData = {}, additionalData = {}) {
    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    // Prevenir duplicados de step view
    if (this.trackedSteps.has(stepName)) {
      console.log(`Step ${stepName} already tracked, skipping view event`);
      return;
    }

    const clientDataCopy = this.sanitizeQuotationData(quotationData);
    const stepInfo = getStepInfo(stepName);

    this.posthog.capture(TRACKING_CONFIG.EVENTS.STEP_VIEWED, {
      stepName,
      stepNumber: stepInfo.number,
      stepTitle: stepInfo.title,
      stepDescription: stepInfo.description,
      clientData: clientDataCopy,
      ...additionalData
    });

    // Marcar como trackeado
    this.trackedSteps.add(stepName);
  }

  /**
   * Track step completion
   * @param {string} stepName - Name of the step completed
   * @param {Object} quotationData - Current quotation data
   * @param {Object} additionalData - Additional data to include in tracking
   */
  trackStepCompleted(stepName, quotationData = {}, additionalData = {}) {
    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    // Prevenir duplicados de step completion
    if (this.trackedCompletions.has(stepName)) {
      console.log(`Step ${stepName} completion already tracked, skipping completion event`);
      return;
    }

    const clientDataCopy = this.sanitizeQuotationData(quotationData);
    const stepInfo = getStepInfo(stepName);

    this.posthog.capture(TRACKING_CONFIG.EVENTS.STEP_COMPLETED, {
      stepName,
      stepNumber: stepInfo.number,
      stepTitle: stepInfo.title,
      stepDescription: stepInfo.description,
      clientData: clientDataCopy,
      ...additionalData
    });

    console.log('trackStepCompleted', stepName, additionalData);

    // Marcar como completado
    this.trackedCompletions.add(stepName);
  }

  /**
   * Track form submission
   * @param {Object} quotationData - Final quotation data
   * @param {Object} additionalData - Additional data to include in tracking
   */
  trackFormSubmission(quotationData = {}, additionalData = {}) {
    if (!this.posthog) {
      console.warn('PostHog not initialized');
      return;
    }

    const clientDataCopy = this.sanitizeQuotationData(quotationData);

    this.posthog.capture(TRACKING_CONFIG.EVENTS.FORM_SUBMITTED, {
      clientData: clientDataCopy,
      ...additionalData
    });
  }

  /**
   * Reset tracking state (useful for testing or form reset)
   */
  resetTracking() {
    this.trackedSteps.clear();
    this.trackedCompletions.clear();
    console.log('Tracking state reset');
  }

  /**
   * Get tracking status
   * @returns {Object} Current tracking status
   */
  getTrackingStatus() {
    return {
      trackedSteps: Array.from(this.trackedSteps),
      trackedCompletions: Array.from(this.trackedCompletions),
      totalStepsTracked: this.trackedSteps.size,
      totalCompletionsTracked: this.trackedCompletions.size
    };
  }

  /**
   * Sanitize quotation data to remove sensitive information
   * @param {Object} quotationData - Raw quotation data
   * @returns {Object} Sanitized quotation data
   */
  sanitizeQuotationData(quotationData) {
    const sanitized = { ...quotationData };

    // Remove sensitive personal information using config
    TRACKING_CONFIG.SENSITIVE_FIELDS.forEach(field => {
      delete sanitized[field];
    });

    return sanitized;
  }

  /**
   * Get step information for tracking
   * @param {string} stepName - Name of the step
   * @returns {Object} Step information
   */
  getStepInfo(stepName) {
    return getStepInfo(stepName);
  }

  /**
   * Get PostHog instance
   * @returns {Object} PostHog instance
   */
  getPostHog() {
    return this.posthog;
  }
}

// Export singleton instance
export default new TrackingService();